import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-hot-toast';
import { ArrowLeft, Package, CheckCircle, Clock, XCircle } from 'lucide-react';
import logger from '../utils/logger';
import axios from 'axios';
import { API_ENDPOINTS } from '../config/api';

const AdminRequestsPage = () => {
  const [requests, setRequests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [pagination, setPagination] = useState({
    currentPage: 1,
    totalPages: 1,
    totalRequests: 0,
    limit: 20,
    hasNextPage: false,
    hasPrevPage: false
  });
  const [filters, setFilters] = useState({
    status: '',
    search: '',
    dateFrom: '',
    dateTo: ''
  });
  const [showCloseModal, setShowCloseModal] = useState(false);
  const [selectedRequest, setSelectedRequest] = useState(null);
  const [finalPrice, setFinalPrice] = useState('');
  const [isClosing, setIsClosing] = useState(false);
  const navigate = useNavigate();


  const fetchRequests = useCallback(async () => {
    try {
      setLoading(true);
      const params = new URLSearchParams({
        page: pagination.currentPage,
        limit: pagination.limit,
        ...filters
      });

      const response = await axios.get(`${API_ENDPOINTS.ADMIN}/requests?${params}`);
      setRequests(response.data.data.requests);
      setPagination(response.data.data.pagination);
    } catch (error) {
      logger.error('Error fetching requests:', error);
      toast.error('שגיאה בטעינת הבקשות');
    } finally {
      setLoading(false);
    }
  }, [pagination.currentPage, pagination.limit, filters]);
  
  useEffect(() => {
    fetchRequests();
  }, [pagination.currentPage, filters, fetchRequests]);


  const handleCloseRequest = async () => {
    if (!selectedRequest) return;

    setIsClosing(true);
    try {
      await axios.post(`${API_ENDPOINTS.ADMIN}/requests/${selectedRequest._id}/close`, {
        finalPrice: finalPrice ? parseFloat(finalPrice) : undefined
      });

      toast.success('הבקשה נסגרה בהצלחה!');
      setShowCloseModal(false);
      setSelectedRequest(null);
      setFinalPrice('');
      fetchRequests();
    } catch (error) {
      logger.error('Error closing request:', error);
      toast.error(error.response?.data?.message || 'שגיאה בסגירת הבקשה');
    } finally {
      setIsClosing(false);
    }
  };

  const openCloseModal = (request) => {
    if (request.status !== 'offer_accepted') {
      toast.error('ניתן לסגור רק בקשות עם הצעה מקובלת');
      return;
    }
    setSelectedRequest(request);
    setFinalPrice(request.selectedPrice?.toString() || '');
    setShowCloseModal(true);
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'new':
        return <Clock className="h-4 w-4 text-orange-600" />;
      case 'in_progress':
        return <Clock className="h-4 w-4 text-blue-600" />;
      case 'offer_accepted':
        return <CheckCircle className="h-4 w-4 text-green-600" />;
      case 'closed':
        return <CheckCircle className="h-4 w-4 text-success-600" />;
      case 'cancelled':
        return <XCircle className="h-4 w-4 text-red-600" />;
      default:
        return <Clock className="h-4 w-4 text-gray-600" />;
    }
  };

  const getStatusText = (status) => {
    switch (status) {
      case 'new':
        return 'חדשה';
      case 'in_progress':
        return 'בטיפול';
      case 'offer_accepted':
        return 'הצעה מקובלת';
      case 'closed':
        return 'הושלמה';
      case 'cancelled':
        return 'בוטלה';
      default:
        return status;
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'new':
        return 'text-orange-600 bg-orange-50';
      case 'in_progress':
        return 'text-blue-600 bg-blue-50';
      case 'offer_accepted':
        return 'text-green-600 bg-green-50';
      case 'closed':
        return 'text-success-600 bg-success-50';
      case 'cancelled':
        return 'text-red-600 bg-red-50';
      default:
        return 'text-gray-600 bg-gray-50';
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-orange-50 to-brown-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-16 w-16 border-b-2 border-orange-600 mx-auto mb-4"></div>
          <p className="text-brown-700 text-lg">טוען...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-orange-50 to-brown-50">
      {/* Header */}
      <header id="navigation" className="header-gradient shadow-lg" tabIndex="-1">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center py-6">
            <div className="flex items-center">
              <button
                onClick={() => navigate('/admin')}
                className="text-white hover:text-orange-100 mr-4"
                aria-label="חזור לדשבורד"
              >
                <ArrowLeft className="h-6 w-6" aria-hidden="true" />
              </button>
              <Package className="h-8 w-8 text-white ml-2" />
              <h1 className="text-2xl sm:text-3xl font-bold text-white">ניהול בקשות</h1>
            </div>
          </div>
        </div>
      </header>

      <main id="main-content" className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12" tabIndex="-1">
        {/* Filters */}
        <div className="card mb-8">
          <h2 className="text-xl font-bold text-brown-900 mb-6">סינון בקשות</h2>
          <div className="grid md:grid-cols-4 gap-4">
            <div>
              <label htmlFor="filter-status" className="form-label">סטטוס</label>
              <select
                id="filter-status"
                value={filters.status}
                onChange={(e) => setFilters({ ...filters, status: e.target.value })}
                className="input-field"
              >
                <option value="">כל הסטטוסים</option>
                <option value="new">חדשה</option>
                <option value="in_progress">בטיפול</option>
                <option value="offer_accepted">הצעה מקובלת</option>
                <option value="closed">הושלמה</option>
                <option value="cancelled">בוטלה</option>
              </select>
            </div>
            
            <div>
              <label htmlFor="filter-search" className="form-label">חיפוש</label>
              <input
                id="filter-search"
                type="text"
                value={filters.search}
                onChange={(e) => setFilters({ ...filters, search: e.target.value })}
                className="input-field"
                placeholder="שם לקוח, טלפון או מספר בקשה"
              />
            </div>
            
            <div>
              <label htmlFor="filter-date-from" className="form-label">מתאריך</label>
              <input
                id="filter-date-from"
                type="date"
                value={filters.dateFrom}
                onChange={(e) => setFilters({ ...filters, dateFrom: e.target.value })}
                className="input-field"
              />
            </div>
            
            <div>
              <label htmlFor="filter-date-to" className="form-label">עד תאריך</label>
              <input
                id="filter-date-to"
                type="date"
                value={filters.dateTo}
                onChange={(e) => setFilters({ ...filters, dateTo: e.target.value })}
                className="input-field"
              />
            </div>
          </div>
        </div>

        {/* Requests List */}
        <div className="space-y-6">
          {requests.length === 0 ? (
            <div className="card text-center py-12">
              <Package className="h-12 w-12 text-brown-300 mx-auto mb-4" />
              <h3 className="text-lg font-semibold text-brown-700 mb-2">אין בקשות</h3>
              <p className="text-brown-600">לא נמצאו בקשות התואמות לסינון</p>
            </div>
          ) : (
            requests.map((request) => (
              <div key={request._id} className="card hover-lift">
                <div className="flex justify-between items-start mb-4">
                  <div>
                    <h3 className="text-xl font-bold text-brown-900 mb-2">
                      בקשה #{request.requestId}
                    </h3>
                    <p className="text-brown-700 mb-1">
                      <strong>לקוח:</strong> {request.customerName}
                    </p>
                    <p className="text-brown-700 mb-1">
                      <strong>טלפון:</strong> {request.customerPhone}
                    </p>
                    <p className="text-brown-700 mb-1">
                      <strong>תאריך יצירה:</strong> {new Date(request.createdAt).toLocaleDateString('he-IL')}
                    </p>
                    {request.selectedDriverName && (
                      <p className="text-brown-700 mb-1">
                        <strong>מוביל נבחר:</strong> {request.selectedDriverName}
                      </p>
                    )}
                    {request.selectedPrice && (
                      <p className="text-brown-700 mb-1">
                        <strong>מחיר:</strong> ₪{request.selectedPrice}
                      </p>
                    )}
                    {request.category && (
                      <p className="text-brown-700 mb-1">
                        <strong>סוג הובלה:</strong>{' '}
                        <span className="inline-block bg-blue-100 text-blue-800 text-sm font-semibold px-2 py-1 rounded-full">
                          {request.category}
                        </span>
                      </p>
                    )}
                  </div>
                  
                  <div className="flex items-center space-x-3 space-x-reverse">
                    <span className={`px-3 py-1 rounded-full text-sm font-semibold flex items-center ${getStatusColor(request.status)}`}>
                      {getStatusIcon(request.status)}
                      <span className="mr-1">{getStatusText(request.status)}</span>
                    </span>
                    
                    {request.status === 'offer_accepted' && (
                      <button
                        onClick={() => openCloseModal(request)}
                        className="btn-primary flex items-center space-x-2 space-x-reverse"
                      >
                        <CheckCircle className="h-4 w-4" />
                        <span>סגור בקשה</span>
                      </button>
                    )}
                  </div>
                </div>
                
                <div className="grid md:grid-cols-2 gap-4 text-sm">
                  <div>
                    <p className="text-brown-600 mb-1">ממוצא:</p>
                    <p className="text-brown-700">{request.originAddress?.street || request.originAddress || 'לא ידוע'}</p>
                  </div>
                  <div>
                    <p className="text-brown-600 mb-1">ליעד:</p>
                    <p className="text-brown-700">{request.destinationAddress?.street || request.destinationAddress || 'לא ידוע'}</p>
                  </div>
                </div>
              </div>
            ))
          )}
        </div>

        {/* Pagination */}
        {pagination.totalPages > 1 && (
          <div className="flex justify-center items-center space-x-2 space-x-reverse mt-8">
            <button
              onClick={() => setPagination({ ...pagination, currentPage: pagination.currentPage - 1 })}
              disabled={!pagination.hasPrevPage}
              className="btn-secondary px-4 py-2 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              הקודם
            </button>
            
            <span className="text-brown-700 font-medium">
              עמוד {pagination.currentPage} מתוך {pagination.totalPages}
            </span>
            
            <button
              onClick={() => setPagination({ ...pagination, currentPage: pagination.currentPage + 1 })}
              disabled={!pagination.hasNextPage}
              className="btn-secondary px-4 py-2 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              הבא
            </button>
          </div>
        )}
      </main>

      {/* Close Request Modal */}
      {showCloseModal && selectedRequest && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl p-8 max-w-md w-full">
            <h3 className="text-xl font-bold text-brown-900 mb-6 text-center">סגור בקשה</h3>
            
            <div className="mb-6 p-4 bg-green-50 rounded-lg">
              <p className="text-green-700 text-center">
                <strong>בקשה:</strong> #{selectedRequest.requestId}
              </p>
              <p className="text-green-700 text-center mt-1">
                <strong>לקוח:</strong> {selectedRequest.customerName}
              </p>
              <p className="text-green-700 text-center mt-1">
                <strong>מוביל:</strong> {selectedRequest.selectedDriverName}
              </p>
              <p className="text-green-700 text-center mt-1">
                <strong>מחיר מוצע:</strong> ₪{selectedRequest.selectedPrice}
              </p>
            </div>

            <div className="mb-6">
              <label htmlFor="final-price" className="form-label">מחיר סופי (אופציונלי)</label>
              <input
                id="final-price"
                type="number"
                value={finalPrice}
                onChange={(e) => setFinalPrice(e.target.value)}
                className="input-field"
                placeholder={`₪${selectedRequest.selectedPrice}`}
                min="0"
                step="0.01"
              />
              <p className="text-sm text-brown-500 mt-1">
                השאר ריק אם המחיר לא השתנה
              </p>
            </div>

            <p className="text-brown-700 mb-6 text-center">
              האם אתה בטוח שברצונך לסגור את הבקשה?
            </p>
            
            <div className="flex space-x-3 space-x-reverse">
              <button
                type="button"
                onClick={() => {
                  setShowCloseModal(false);
                  setSelectedRequest(null);
                  setFinalPrice('');
                }}
                className="btn-secondary flex-1"
                disabled={isClosing}
              >
                ביטול
              </button>
              <button
                onClick={handleCloseRequest}
                className="btn-primary flex-1"
                disabled={isClosing}
              >
                {isClosing ? 'סוגר...' : 'סגור בקשה'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminRequestsPage;
